/**
 * Contratos de la integración con Google Business Profile.
 *
 * Espejo de lo que devuelve `marketing/google_business` en el backend
 * (plick-monorepo-panel-comercio · src/plick-pc-integrations).
 */

/** Cómo quedó vinculada la ficha. Define qué puede hacer el comercio. */
export type GoogleLinkMode = 'PLACE_ID' | 'AGENCY_MANAGED' | 'OAUTH_OWNER';

/** Modos que permiten escribir en la ficha (responder reseñas, publicar, etc.). */
export const WRITE_MODES: GoogleLinkMode[] = ['AGENCY_MANAGED', 'OAUTH_OWNER'];

/**
 * Estados del modelo canónico de salud de integración.
 * Se lee de `configuration.health.state`.
 */
export type IntegrationHealthState =
  | 'PENDING'
  | 'HEALTHY'
  | 'DEGRADED'
  | 'AUTH_FAILED'
  | 'UNREACHABLE'
  | 'DISCONNECTED'
  | 'REVOKED';

/** Qué debe hacer el comercio. Determina el CTA del banner. */
export type IntegrationUserAction =
  | 'UPDATE_CREDENTIALS'
  | 'RECONNECT'
  | 'CONTACT_PROVIDER'
  | 'CONTACT_SUPPORT'
  | 'WAIT'
  | 'NONE';

export interface IntegrationHealth {
  state: IntegrationHealthState;
  checked_at: string;
  checked_by: 'probe' | 'runtime' | 'webhook' | 'user';
  last_healthy_at: string | null;
  message: string | null;
  error_code: string | null;
  error_detail: string | null;
  user_action: IntegrationUserAction;
  action_url: string | null;
  consecutive_failures: number;
  details?: Record<string, unknown>;
}

/** Un candidato de la búsqueda de fichas. */
export interface GooglePlaceCandidate {
  place_id: string;
  title: string;
  address: string;
  rating: number | null;
  user_rating_count: number;
  business_status: string | null;
  /**
   * Metros entre esta ficha y el domicilio registrado del comercio, o `null` si
   * falta cualquiera de las dos coordenadas.
   *
   * `null` significa "no se puede saber", NO "está cerca": son casos distintos y
   * el aviso solo debe dispararse cuando hay dato. Es la única señal de sucursal
   * equivocada que no depende del ranking de Google, que ya demostró colapsar a
   * la sucursal más prominente incluso con restricción geográfica.
   */
  distance_m: number | null;
}

/** Una ficha administrable por la cuenta conectada. */
export interface GoogleManageableLocation {
  name: string;
  title: string;
  address: string;
  phone: string;
  website: string;
  place_id: string | null;
  maps_url: string | null;
  account_name: string;
  account_label: string;
}

export interface GoogleReview {
  review_id?: string;
  name?: string;
  author: string;
  author_photo: string;
  rating: number | null;
  comment?: string;
  text?: string;
  relative_time?: string;
  create_time?: string | null;
  update_time?: string | null;
  reply?: { comment: string; update_time: string | null } | null;
}

export interface GoogleReviewsResponse {
  average_rating: number | null;
  total: number;
  reviews: GoogleReview[];
  /** true cuando Google solo comparte una muestra (modo de solo lectura). */
  partial: boolean;
  can_reply: boolean;
  partial_reason?: string;
  next_page_token?: string | null;
}

export interface GoogleMetricSeries {
  metric: string;
  total: number;
  series: { date: string; value: number }[];
}

export interface GoogleMetricsResponse {
  range: { start: string; end: string; days: number };
  metrics: GoogleMetricSeries[];
}

/**
 * Configuración persistida. Los tokens NUNCA llegan al navegador: el backend los
 * sustituye por banderas `has_*` en la frontera de respuesta.
 */
export interface GoogleBusinessConfiguration {
  link_mode?: GoogleLinkMode | null;
  public_place_id?: string | null;
  location_name?: string | null;
  account_name?: string | null;
  title?: string;
  address?: string;
  phone?: string;
  website?: string;
  maps_url?: string | null;
  rating?: number | null;
  user_rating_count?: number;
  business_status?: string | null;
  order_link_uri?: string;
  last_synced_at?: string;
  has_refresh_token?: boolean;
  available_accounts?: { name: string; account_name: string }[];
  health?: IntegrationHealth;
}

/** Acciones soportadas por el dispatcher del backend. */
export const GOOGLE_ACTIONS = {
  SEARCH_PLACE: 'SEARCH_PLACE',
  TEST_CONNECTION: 'TEST_CONNECTION',
  CONNECT: 'CONNECT',
  DISCONNECT: 'DISCONNECT',
  SHOW_STATUS: 'SHOW_STATUS',
  SYNC_PROFILE: 'SYNC_PROFILE',
  PUSH_PROFILE: 'PUSH_PROFILE',
  LIST_REVIEWS: 'LIST_REVIEWS',
  REPLY_REVIEW: 'REPLY_REVIEW',
  GET_METRICS: 'GET_METRICS',
  CREATE_POST: 'CREATE_POST',
  SET_ORDER_LINK: 'SET_ORDER_LINK',
  LIST_MANAGEABLE_LOCATIONS: 'LIST_MANAGEABLE_LOCATIONS',
  SELECT_LOCATION: 'SELECT_LOCATION',
} as const;

/** Etiquetas de las métricas de desempeño que devuelve Google. */
export const METRIC_LABELS: Record<string, string> = {
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: 'Vistas en Maps (escritorio)',
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: 'Vistas en Búsqueda (escritorio)',
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: 'Vistas en Maps (móvil)',
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: 'Vistas en Búsqueda (móvil)',
  BUSINESS_CONVERSATIONS: 'Mensajes recibidos',
  BUSINESS_DIRECTION_REQUESTS: 'Solicitudes de ruta',
  CALL_CLICKS: 'Llamadas',
  WEBSITE_CLICKS: 'Clics al sitio web',
};
